import { createArticleRequestSchema, type CreateArticleRequest } from '@gb-ticket/shared-types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ApiRequestError } from '@/lib/api';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { useArticleGroups, useCreateArticle } from '@/platform/features/articles/use-articles';
import { useCostCenters, useUnits } from '@/platform/features/master-data/use-master-data';
import { articlePath } from '@/platform/lib/article-paths';
import { useVoiceCopy } from '@/platform/voice/use-voice';

const NONE = 'none';

function blankToNull(value: string | null): string | null {
  if (value == null) {
    return null;
  }
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

export function ArticleFormPage() {
  const copy = useVoiceCopy();
  const navigate = useNavigate();
  const createArticle = useCreateArticle();
  const groups = useArticleGroups(false);
  const units = useUnits();
  const costCenters = useCostCenters();
  const form = useForm<CreateArticleRequest>({
    resolver: zodResolver(createArticleRequestSchema),
    defaultValues: {
      name: '',
      validFrom: '',
      type: 'material',
      groupId: '',
      baseUnitId: '',
      purchaseUnitId: '',
      stockManaged: false,
      taxClass: 'standard',
      costCenterId: null,
    },
  });

  async function onValid(values: CreateArticleRequest) {
    try {
      const created = await createArticle.mutateAsync({
        ...values,
        costCenterId: values.costCenterId ?? null,
      });
      toast.success('Artikel angelegt.');
      void navigate(articlePath(created.id));
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Der Artikel konnte nicht angelegt werden.',
      );
    }
  }

  const errors = form.formState.errors;
  const type = form.watch('type');

  return (
    <section className="max-w-xl space-y-6">
      <PlatformPageHeader
        title="Artikel anlegen"
        description="Die Artikelnummer wird vergeben. Eine Dienstleistung kann nicht lagergeführt sein."
      />
      <form className="space-y-4" onSubmit={form.handleSubmit(onValid)} noValidate>
        <div className="space-y-2">
          <Label htmlFor="name">Bezeichnung</Label>
          <Input
            id="name"
            {...form.register('name')}
            aria-invalid={errors.name ? true : undefined}
          />
          {errors.name ? (
            <p role="alert" className="text-sm text-destructive">
              {copy('platform.form.designation')}
            </p>
          ) : null}
        </div>
        <div className="space-y-2">
          <Label htmlFor="validFrom">Gültig ab</Label>
          <Input
            id="validFrom"
            placeholder="JJJJ-MM-TT"
            {...form.register('validFrom')}
            aria-invalid={errors.validFrom ? true : undefined}
          />
          {errors.validFrom ? (
            <p role="alert" className="text-sm text-destructive">
              {copy('platform.form.date')}
            </p>
          ) : null}
        </div>
        <div className="space-y-2">
          <Label htmlFor="type">Typ</Label>
          <Select
            value={form.watch('type')}
            onValueChange={(value) => {
              void form.setValue('type', value as 'material' | 'service');
              if (value === 'service') {
                form.setValue('stockManaged', false);
              }
            }}
          >
            <SelectTrigger id="type" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="material">Material</SelectItem>
              <SelectItem value="service">Dienstleistung</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="groupId">Gruppe</Label>
          <Select
            value={form.watch('groupId') || NONE}
            onValueChange={(value) =>
              form.setValue('groupId', value === NONE ? '' : value, { shouldValidate: true })
            }
          >
            <SelectTrigger
              id="groupId"
              className="w-full"
              aria-invalid={errors.groupId ? true : undefined}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Bitte wählen</SelectItem>
              {groups.data?.items.map((group) => (
                <SelectItem key={group.id} value={group.id}>
                  {group.code} · {group.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.groupId ? (
            <p role="alert" className="text-sm text-destructive">
              {copy('platform.form.article-group')}
            </p>
          ) : null}
        </div>
        <div className="space-y-2">
          <Label htmlFor="baseUnitId">Basiseinheit</Label>
          <Select
            value={form.watch('baseUnitId') || NONE}
            onValueChange={(value) =>
              form.setValue('baseUnitId', value === NONE ? '' : value, { shouldValidate: true })
            }
          >
            <SelectTrigger
              id="baseUnitId"
              className="w-full"
              aria-invalid={errors.baseUnitId ? true : undefined}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Bitte wählen</SelectItem>
              {units.data?.items
                .filter((unit) => unit.isActive)
                .map((unit) => (
                  <SelectItem key={unit.id} value={unit.id}>
                    {unit.code} · {unit.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          {errors.baseUnitId ? (
            <p role="alert" className="text-sm text-destructive">
              {copy('platform.form.base-unit')}
            </p>
          ) : null}
        </div>
        <div className="space-y-2">
          <Label htmlFor="purchaseUnitId">Einkaufseinheit</Label>
          <Select
            value={form.watch('purchaseUnitId') || NONE}
            onValueChange={(value) =>
              form.setValue('purchaseUnitId', value === NONE ? '' : value, { shouldValidate: true })
            }
          >
            <SelectTrigger
              id="purchaseUnitId"
              className="w-full"
              aria-invalid={errors.purchaseUnitId ? true : undefined}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Bitte wählen</SelectItem>
              {units.data?.items
                .filter((unit) => unit.isActive)
                .map((unit) => (
                  <SelectItem key={unit.id} value={unit.id}>
                    {unit.code} · {unit.name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          {errors.purchaseUnitId ? (
            <p role="alert" className="text-sm text-destructive">
              {copy('platform.form.purchase-unit')}
            </p>
          ) : null}
        </div>
        <div className="space-y-2">
          <Label htmlFor="taxClass">Steuerklasse</Label>
          <Select
            value={form.watch('taxClass')}
            onValueChange={(value) =>
              form.setValue('taxClass', value as CreateArticleRequest['taxClass'])
            }
          >
            <SelectTrigger id="taxClass" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="standard">Standard</SelectItem>
              <SelectItem value="reduced">Ermäßigt</SelectItem>
              <SelectItem value="zero">Null</SelectItem>
              <SelectItem value="exempt">Befreit</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="costCenterId">Kostenstelle</Label>
          <Select
            value={form.watch('costCenterId') ?? NONE}
            onValueChange={(value) =>
              form.setValue('costCenterId', blankToNull(value === NONE ? '' : value))
            }
          >
            <SelectTrigger id="costCenterId" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Keine</SelectItem>
              {costCenters.data?.items
                .filter((row) => row.isActive)
                .map((row) => (
                  <SelectItem key={row.id} value={row.id}>
                    {row.code}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <input
            id="stockManaged"
            type="checkbox"
            className="size-4"
            disabled={type === 'service'}
            {...form.register('stockManaged')}
            onChange={(event) => {
              form.setValue('stockManaged', type === 'service' ? false : event.target.checked);
            }}
          />
          <Label htmlFor="stockManaged">Für ein späteres Lager vorgesehen</Label>
        </div>
        {errors.stockManaged ? (
          <p role="alert" className="text-sm text-destructive">
            Eine Dienstleistung kann nicht lagergeführt sein.
          </p>
        ) : null}
        <Button type="submit" disabled={createArticle.isPending}>
          Anlegen
        </Button>
      </form>
    </section>
  );
}
